/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';

declare module '@emotion/react' {
  export interface Theme {
    palette: {
      // Navigation & Layout
      navLinkColor: string;
      layoutBorderBottomColor: string;

      // Background Colors
      tertiaryBgColor: string;
      bgWhiteColor: string;
      pageBgColor: string;
      brandBackdrop: string;
      lightGrayBg: string;
      veryLightGrayBg: string;
      blueGrayBg: string;
      ultraLightGrayBg: string;
      whiteBg: string;
      lightSignInBg: string;
      darkGrayBg: string;
      tableBg: string;

      // Primary & Accent Colors
      logiratePrimary: string;
      logiratePrimaryLight: string;
      blueColor: string;
      primaryBlue: string;
      brightBlue: string;
      deepBlue: string;
      lightBlueOpacity: string;
      greenColor: string;
      successGreen: string;
      lightGreenBg: string;
      darkGreenText: string;
      goldYellow: string;
      lightYellowBg: string;
      orangeAccent: string;

      // Form Highlight Colors
      logiFormBorderHighlight: string;
      logiFormBackgroundHighlight: string;

      // Red/Error Colors
      redFontColor: string;
      redHoverFontColor: string;
      errorRed: string;
      dangerRed: string;
      lightRedBg: string;
      lightPinkBorder: string;
      orangeRed: string;

      // Border Colors
      darkBorderColor: string;
      darkestBorderColor: string;
      borderColor: string;
      lightBorder: string;
      mediumBorder: string;
      grayBorder: string;
      lightGrayBorder: string;
      veryLightBorder: string;
      tableBorder: string;
      iconBorder: string;

      // Text Colors
      blackColor: string;
      logirateBlack: string;
      darkTextColor: string;
      primaryTextColor: string;
      secondaryTextColor: string;
      tertiaryTextColor: string;
      grayTextColor: string;
      lightGrayText: string;
      mediumGrayText: string;
      darkGrayText: string;
      slateGrayText: string;
      formFooterText: string;
      formDescriptionText: string;
      iconGray: string;
      charcoalGray: string;
      paginatorGray: string;

      // Icon Colors
      iconNeutral: string;
      iconDark: string;
      iconMedium: string;
      iconLight: string;

      // Highlight & Neutral Colors
      highlightColor: string;
      neutralGray: string;
      lightNeutralGray: string;
      mediumNeutralGray: string;

      // Status Colors
      successBg: string;
      warningBg: string;
      errorBg: string;
      infoBg: string;

      // Warning Colors
      warningBgColor: string;
      warningTextColor: string;

      // Overlay & Shadow Colors
      modalBackdrop: string;
      lightOverlay: string;
      boxShadowColor: string;
      lightBoxShadowColor: string;
      darkShadow: string;
    };
  }
}

export const theme: Theme = {
  palette: {
    // Navigation & Layout
    navLinkColor: '#1F2B34',
    layoutBorderBottomColor: '#e0e0e0',

    // Background Colors
    tertiaryBgColor: '#FAFBFB',
    bgWhiteColor: '#FFFFFF',
    pageBgColor: '#F7F9FE',
    brandBackdrop: '#E7EFFB',
    lightGrayBg: '#f8f8f8',
    veryLightGrayBg: '#f3f4f6',
    blueGrayBg: '#f7f9fc',
    ultraLightGrayBg: '#f9fafb',
    whiteBg: '#ffffff',
    lightSignInBg: '#f6f9ff',
    darkGrayBg: '#474747',
    tableBg: '#fafafa',

    // Primary & Accent Colors
    logiratePrimary: 'rgba(59, 124, 244, 0.5)',
    logiratePrimaryLight: 'rgba(59, 124, 244, 0.1)',
    blueColor: '#5b91f5',
    primaryBlue: '#3b7cf4',
    brightBlue: '#3c80ff',
    deepBlue: '#436db8',
    lightBlueOpacity: '#dee9fd',
    greenColor: '#00b73f',
    successGreen: '#16a34a',
    lightGreenBg: '#f0fdf4',
    darkGreenText: '#4ade80',
    goldYellow: '#ca8a04',
    lightYellowBg: '#fefce8',
    orangeAccent: '#fb923c',

    // Form Highlight Colors
    logiFormBorderHighlight: 'rgba(244, 111, 59, 0.5)',
    logiFormBackgroundHighlight: 'rgba(59, 124, 244, 0.05)',

    // Red/Error Colors
    redFontColor: '#DA0E29',
    redHoverFontColor: '#c4031d',
    errorRed: '#ff0500',
    dangerRed: '#dc2626',
    lightRedBg: '#fef2f2',
    lightPinkBorder: '#fca5a5',
    orangeRed: '#f46f3b',

    // Border Colors
    darkBorderColor: '#D2D5DA',
    darkestBorderColor: '#9CA3AF',
    borderColor: '#E5E7EB',
    lightBorder: '#f4f4f5',
    mediumBorder: '#dddddd',
    grayBorder: '#d5d5dd',
    lightGrayBorder: '#ededed',
    veryLightBorder: '#f5f5f5',
    tableBorder: '#e2e2e2',
    iconBorder: '#bababa',

    // Text Colors
    blackColor: '#000000',
    logirateBlack: '#232222',
    darkTextColor: '#070707',
    primaryTextColor: '#1f2937',
    secondaryTextColor: '#374151',
    tertiaryTextColor: '#374151',
    grayTextColor: '#6b7280',
    lightGrayText: '#867c79',
    mediumGrayText: '#71748c',
    darkGrayText: '#5c5963',
    slateGrayText: '#4b5563',
    formFooterText: '#81899E',
    formDescriptionText: '#6B7280',
    iconGray: '#6e7079',
    charcoalGray: '#4e4e4e',
    paginatorGray: '#8b8d97',

    // Icon Colors
    iconNeutral: '#41415a',
    iconDark: '#232222',
    iconMedium: '#9ca3af',
    iconLight: '#bababa',

    // Highlight & Neutral Colors
    highlightColor: '#F6F6F6',
    neutralGray: '#41415a',
    lightNeutralGray: '#f1f1f4',
    mediumNeutralGray: '#d9d9d9',

    // Status Colors
    successBg: '#f0fdf4',
    warningBg: '#fefce8',
    errorBg: '#fef2f2',
    infoBg: '#f7fcfd',

    // Warning Colors
    warningBgColor: 'rgba(255, 113, 113, 0.1)',
    warningTextColor: '#FF7171',

    // Overlay & Shadow Colors
    modalBackdrop: 'rgba(0, 0, 0, 0.2);',
    lightOverlay: 'rgba(0, 0, 0, 0.08)',
    boxShadowColor: 'rgba(0, 0, 0, 0.05)',
    lightBoxShadowColor: 'rgba(0, 0, 0, 0.15)',
    darkShadow: 'rgba(0, 0, 0, 0.25)',
  },
};

const GlobalStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  * {
    margin: 0;
    font-size: 1em;
  }
  html,
  body {
    height: 100%;
  }
  body {
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    font-family: 'Manrope', 'Work Sans', sans-serif;
    font-weight: 400;
    color: black;
  }
  img,
  picture,
  video,
  canvas,
  svg {
    display: block;
    max-width: 100%;
  }
  input,
  button,
  textarea,
  select {
    font: inherit;
  }
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    overflow-wrap: break-word;
  }
  #root,
  #__next {
    isolation: isolate;
  }
`;

export default GlobalStyles;
